import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import Alert from '@mui/material/Alert';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DownloadIcon from '@mui/icons-material/Download';

import { API_BASE, LiveBoardRow, PenaltyType } from '../org/scoring-types';
import useWebSocketHandler from '../wsHookHandler';
import ConvertTime from '../converters/timestampConverter';

const CHECKED_ROW_BG = 'rgba(255, 193, 7, 0.18)';
const CHECKED_ROW_HOVER_BG = 'rgba(255, 193, 7, 0.28)';
const CHECKBOX_YELLOW = '#fbc02d';
const RENUMBERED_LAP_COLUMN_LABEL = 'Renumbered Lap #';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type ScoringClass = 'H' | 'E' | null;
type ScoringTab = 'ALL' | 'H' | 'E' | 'LAPS';
type SortDirection = 'asc' | 'desc';

interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

// One row from GET /api/scoring/results — ScoringResults joined with
// Cars metadata. DECIMAL columns come back as strings from mysql2.
interface ScoringResultRow {
  CarNumber: number;
  Institution: string;
  TeamName: string | null;
  VehicleClass: ScoringClass;
  TotalLaps: number | null;
  FreePitLaps: number | null;
  ChargedPitLaps: number | null;
  OnTrackLaps: number | null;
  TotalElapsed: string | null;
  FreePitTime: string | null;
  OnTrackTime: string | null;
  ConePenaltyTime: string | null;
  OCPenaltyTime: string | null;
  ExcessDCPenaltyTime: string | null;
  ExcessTirePenaltyTime: string | null;
  OtherPenaltyTime: string | null;
  TotalPenaltyTime: string | null;
  CorrectedTime: string | null;
  AvgLapTime: string | null;
  LapSum: number | null;
  LapSumMax: number | null;
  DistanceFactor: string | null;
  MaxAvgLapTime: string | null;
  Tmin: string | null;
  SpeedFactor: string | null;
  PerformancePoints: string | null;
  StartPoints: string | null;
  ParticipationPoints: string | null;
  RawScore: string | null;
  TotalPenaltyPoints: string | null;
  FinalScore: string | null;
  ClassRank: number | null;
  CalculatedAt: string | null;
}

interface LapSummaryRow {
  CarNumber: number;
  VehicleClass: ScoringClass;
  TotalLaps: number;
  OnTrackLaps: number;
  FreePitLaps: number;
  ChargedPitLaps: number;
  InvalidLaps: number;
  ExcessLaps: number;
  DriverChanges: number;
  TotalValidTime: string | null;
  FastestLap: string | null;
  SlowestLap: string | null;
  AvgOnTrackLap: string | null;
}

interface LapDetailRow {
  Recnum: number;
  CarNumber: number;
  Transponder: string;
  LapNumber: number;
  LapTime: string;
  StartTime: string | null;
  EndTime: string | null;
  LapType: string;
  IsFree: number | boolean;
  InTimeWindow: number | boolean;
  ElapsedFromStart: string | null;
  Flags: unknown;
}

// Per D7.13.6 — three mandatory driver changes per team. Shown as
// "achieved / required" so teams that DNF still get visible credit
// for the DCs they did complete.
const REQUIRED_DRIVER_CHANGES = 3;

interface PenaltySummaryRow {
  CarNumber: number;
  VehicleClass: ScoringClass;
  ConeCount: number;
  OffCourseCount: number;
  MissedSlalomCount: number;
  FlagDisobeyCount: number;
  OverDrivingCount: number;
  RunOutOfOrderCount: number;
  ExcessDcCount: number;
  ExcessTireCount: number;
  ExtraPersonCount: number;
  EnergyViolationCount: number;
  ContactDqCount: number;
  OtherCount: number;
  TotalCount: number;
  TotalTimePenalty: string | null;
  TotalPointPenalty: string | null;
}

interface CalculateResponse {
  calculated: number;
  skipped: { CarNumber: number; reason: string }[];
}

type ScoringWsMessage =
  | { event: 'calculated'; calculated: number; skipped: unknown[]; at: string }
  | { event: string; [k: string]: unknown };

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------
function num(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

// H:MM:SS.s for corrected / elapsed times. Drops the hour segment when zero.
function formatLongTime(v: string | number | null | undefined): string {
  const n = num(v);
  if (n == null) return '—';
  const hours = Math.floor(n / 3600);
  const mins = Math.floor((n - hours * 3600) / 60);
  const secs = n - hours * 3600 - mins * 60;
  const pad = (x: number) => x.toString().padStart(2, '0');
  const secStr = secs.toFixed(1).padStart(4, '0');
  if (hours > 0) return `${hours}:${pad(mins)}:${secStr}`;
  return `${mins}:${secStr}`;
}

// M:SS.s for average / individual lap times.
function formatLapTime(v: string | number | null | undefined): string {
  const n = num(v);
  if (n == null || n <= 0) return '—';
  const mins = Math.floor(n / 60);
  const secs = n - mins * 60;
  return `${mins}:${secs.toFixed(1).padStart(4, '0')}`;
}

function formatDurationTime(v: string | number | null | undefined): string {
  const n = num(v);
  if (n == null) return '—';
  const totalMilliseconds = Math.round(Math.abs(n) * 1000);
  const sign = n < 0 ? '-' : '';
  const hours = Math.floor(totalMilliseconds / 3_600_000);
  const minutes = Math.floor((totalMilliseconds % 3_600_000) / 60_000);
  const seconds = Math.floor((totalMilliseconds % 60_000) / 1000);
  const milliseconds = totalMilliseconds % 1000;
  return `${sign}${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds
    .toString()
    .padStart(3, '0')}`;
}

function formatNumber(
  v: string | number | null | undefined,
  digits = 2,
): string {
  const n = num(v);
  return n == null ? '—' : n.toFixed(digits);
}

const PENALTY_LABELS: Record<PenaltyType, string> = {
  CONE: 'Cone',
  OFF_COURSE: 'Off-Course',
  MISSED_SLALOM: 'Missed Slalom',
  FLAG_DISOBEY: 'Flag Disobey',
  OVER_DRIVING: 'Over-Driving',
  RUN_OUT_OF_ORDER: 'Out of Order',
  CONTACT_DQ: 'Contact DQ',
  EXCESS_DC_TIME: 'Excess DC',
  EXCESS_TIRE_TIME: 'Excess Tire',
  EXTRA_PERSON: 'Extra Person',
  ENERGY_VIOLATION: 'Energy Violation',
  OTHER: 'Other',
};

const PENALTY_COUNT_FIELDS: { key: keyof PenaltySummaryRow; label: string }[] = [
  { key: 'ConeCount', label: PENALTY_LABELS.CONE },
  { key: 'OffCourseCount', label: PENALTY_LABELS.OFF_COURSE },
  { key: 'MissedSlalomCount', label: PENALTY_LABELS.MISSED_SLALOM },
  { key: 'FlagDisobeyCount', label: PENALTY_LABELS.FLAG_DISOBEY },
  { key: 'OverDrivingCount', label: PENALTY_LABELS.OVER_DRIVING },
  { key: 'RunOutOfOrderCount', label: PENALTY_LABELS.RUN_OUT_OF_ORDER },
  { key: 'ExcessDcCount', label: PENALTY_LABELS.EXCESS_DC_TIME },
  { key: 'ExcessTireCount', label: PENALTY_LABELS.EXCESS_TIRE_TIME },
  { key: 'ExtraPersonCount', label: PENALTY_LABELS.EXTRA_PERSON },
  { key: 'EnergyViolationCount', label: PENALTY_LABELS.ENERGY_VIOLATION },
  { key: 'ContactDqCount', label: PENALTY_LABELS.CONTACT_DQ },
  { key: 'OtherCount', label: PENALTY_LABELS.OTHER },
];

const carFilter = createFilterOptions<LiveBoardRow>({
  stringify: (c) => `${c.CarNumber} ${c.Institution}`,
  trim: true,
  ignoreCase: true,
});

const FIELD_DEFINITIONS: Record<string, string> = {
  Expand: 'Open this car row to see the lap, penalty, and scoring audit breakdowns.',
  'Lap breakdown': 'Lap-count and lap-time details used to audit this car’s scoring inputs.',
  'Penalty breakdown': 'Penalty counts, time penalties, and point penalties applied to this car.',
  'Scoring audit': 'Intermediate scoring formula values used to produce the final score.',
  Rank: 'Class rank after final scoring. Lower rank is better.',
  Car: 'The car number assigned to the team.',
  'Team / Institution': 'The team name when available, plus the institution associated with the car.',
  Class: 'Vehicle class used for scoring and ranking.',
  Laps: 'Scored on-track laps for this car, excluding free pit laps and invalid scoring laps.',
  Corrected: 'Corrected total time after subtracting free pit time and applying time penalties.',
  'Avg Lap': 'Corrected time divided by scored on-track laps.',
  'Penalty Time': 'Total time penalties, in seconds, applied to corrected time.',
  'Penalty Pts': 'Total point penalties applied after raw score calculation.',
  'Final Score': 'Final endurance score after performance, start, participation, and penalty points.',
  'Last calculated': 'Time when scoring results were last calculated, shown in Eastern time.',
  Total: 'Total lap rows for this car in the scoring lap summary.',
  'On-Track': 'Valid laps classified as normal racing laps.',
  'Free Pit': 'Pit laps that are excluded from corrected racing time.',
  'Charged Pit': 'Pit laps that count against the team instead of being free.',
  'Driver Changes': 'Completed driver-change pit events compared with the required count.',
  Invalid: 'Lap rows outside the scoring time limit. They remain visible for audit but do not score.',
  'Excess (post-race)': 'Valid on-track laps after the race-distance limit. They remain visible for audit but do not score.',
  Fastest: 'Fastest valid on-track lap for this car.',
  Slowest: 'Slowest valid on-track lap for this car.',
  'Avg on-track': 'Average of valid on-track lap times before scoring penalties.',
  'Time total': 'Total penalty time applied to this car.',
  Points: 'Total penalty points applied to this car.',
  LapSum: 'Number of counted laps compared with the maximum lap count used for the distance factor.',
  'Distance Factor': 'Distance adjustment based on counted laps versus the maximum counted laps.',
  Tmin: 'Fastest average lap time in the scoring set for this class/result set.',
  MaxAvgLap: 'Configured maximum average lap time used as the slow-end bound for speed factor.',
  'Speed Factor': 'Speed adjustment based on average lap time between Tmin and MaxAvgLap.',
  'Performance Pts': 'Performance portion of the endurance score after distance and speed factors.',
  'Start Pts': 'Points awarded for starting or meeting start criteria.',
  'Participation Pts': 'Participation points awarded by the scoring rules.',
  'Raw Score': 'Score before final point penalties are applied.',
  Recnum: 'Database record number for this lap row.',
  Transponder: 'Transponder ID that produced the lap.',
  'Lap #': 'Lap sequence number after sensor-noise hits are filtered out.',
  [RENUMBERED_LAP_COLUMN_LABEL]: 'Sequential lap number generated from the checked rows only. Driver-change laps are left blank.',
  LapTime: 'Elapsed time between accepted transponder passings for this lap.',
  StartTime: 'Accepted passing time that started this lap, shown in Eastern time.',
  EndTime: 'Accepted passing time that ended this lap, shown in Eastern time.',
  LapType: 'Lap classification used for scoring, such as ON_TRACK or a pit-lap type.',
  IsFree: 'Whether this lap is treated as a free pit lap for scoring.',
  InTimeWindow: 'Whether this lap ended inside the 120-minute event time window. Rows outside the window remain visible for audit but do not score.',
  ElapsedFromStart: 'Elapsed time from the car’s first accepted passing to this lap end.',
  Flags: 'Audit flags attached by scoring logic, such as laps beyond race distance.',
  Cone: 'Cone penalty count.',
  'Off-Course': 'Off-course penalty count.',
  'Missed Slalom': 'Missed slalom penalty count.',
  'Flag Disobey': 'Flag-disobey penalty count.',
  'Over-Driving': 'Over-driving penalty count.',
  'Out of Order': 'Run-out-of-order penalty count.',
  'Excess DC': 'Driver-change excess-time penalty count.',
  'Excess Tire': 'Tire-change excess-time penalty count.',
  'Extra Person': 'Extra-person penalty count.',
  'Energy Violation': 'Energy violation penalty count.',
  'Contact DQ': 'Contact disqualification penalty count.',
  Other: 'Other manually-entered penalty count.',
};

function DefinedLabel({ label, definitionKey }: { label: React.ReactNode; definitionKey?: string }) {
  const key = definitionKey ?? (typeof label === 'string' ? label : undefined);
  const title = key ? FIELD_DEFINITIONS[key] : undefined;
  if (!title) return <>{label}</>;
  return (
    <Tooltip title={title} arrow>
      <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
        {label}
      </Box>
    </Tooltip>
  );
}

function nextSortState<K extends string>(
  current: SortState<K> | null,
  key: K,
): SortState<K> {
  return {
    key,
    direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  };
}

function compareNullableValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  const aNum = typeof a === 'number' ? a : typeof a === 'string' && a.trim() !== '' ? Number(a) : NaN;
  const bNum = typeof b === 'number' ? b : typeof b === 'string' && b.trim() !== '' ? Number(b) : NaN;
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function sortByState<T, K extends string>(
  rows: T[],
  sort: SortState<K> | null,
  getValue: (row: T, key: K) => unknown,
): T[] {
  if (!sort) return rows;
  const direction = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => direction * compareNullableValues(getValue(a, sort.key), getValue(b, sort.key)));
}

// ---------------------------------------------------------------------------
// Audit expansion row
// ---------------------------------------------------------------------------
interface AuditRowProps {
  row: ScoringResultRow;
  lap: LapSummaryRow | undefined;
  penalty: PenaltySummaryRow | undefined;
}

function AuditRow({ row, lap, penalty }: AuditRowProps) {
  const kv = (label: string, value: React.ReactNode) => (
    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        <DefinedLabel label={label} />
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {value}
      </Typography>
    </Stack>
  );

  const penaltyChips = penalty
    ? PENALTY_COUNT_FIELDS.filter((f) => Number(penalty[f.key]) > 0).map((f) => (
        <Chip
          key={f.key}
          size="small"
          label={<DefinedLabel label={`${f.label}: ${penalty[f.key]}`} definitionKey={f.label} />}
          variant="outlined"
        />
      ))
    : [];

  return (
    <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            <DefinedLabel label="Lap breakdown" />
          </Typography>
          <Stack
            direction="row"
            spacing={3}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.5 }}
          >
            {kv('Total', lap?.TotalLaps ?? row.TotalLaps ?? '—')}
            {kv('On-Track', lap?.OnTrackLaps ?? row.OnTrackLaps ?? '—')}
            {kv('Free Pit', lap?.FreePitLaps ?? row.FreePitLaps ?? '—')}
            {kv('Charged Pit', lap?.ChargedPitLaps ?? row.ChargedPitLaps ?? '—')}
            {kv(
              'Driver Changes',
              `${lap?.DriverChanges ?? 0} / ${REQUIRED_DRIVER_CHANGES}`,
            )}
            {kv('Invalid', lap?.InvalidLaps ?? 0)}
            {(lap?.ExcessLaps ?? 0) > 0 &&
              kv('Excess (post-race)', lap?.ExcessLaps ?? 0)}
            {kv('Fastest', formatLapTime(lap?.FastestLap))}
            {kv('Slowest', formatLapTime(lap?.SlowestLap))}
            {kv('Avg on-track', formatLapTime(lap?.AvgOnTrackLap))}
          </Stack>
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary">
            <DefinedLabel label="Penalty breakdown" />
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.5 }}
          >
            {penaltyChips.length > 0 ? (
              penaltyChips
            ) : (
              <Typography variant="body2" color="text.secondary">
                No penalties.
              </Typography>
            )}
            {kv('Time total', `${formatNumber(row.TotalPenaltyTime, 2)} s`)}
            {kv('Points', formatNumber(row.TotalPenaltyPoints, 2))}
          </Stack>
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary">
            <DefinedLabel label="Scoring audit" />
          </Typography>
          <Stack
            direction="row"
            spacing={3}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.5 }}
          >
            {kv('LapSum', `${row.LapSum ?? '—'} / ${row.LapSumMax ?? '—'}`)}
            {kv('Distance Factor', formatNumber(row.DistanceFactor, 4))}
            {kv('Tmin', `${formatLapTime(row.Tmin)} (${formatNumber(row.Tmin, 3)} s)`)}
            {kv('MaxAvgLap', `${formatLapTime(row.MaxAvgLapTime)} (${formatNumber(row.MaxAvgLapTime, 3)} s)`)}
            {kv('Speed Factor', formatNumber(row.SpeedFactor, 4))}
            {kv('Performance Pts', formatNumber(row.PerformancePoints, 2))}
            {kv('Start Pts', formatNumber(row.StartPoints, 2))}
            {kv('Participation Pts', formatNumber(row.ParticipationPoints, 2))}
            {kv('Raw Score', formatNumber(row.RawScore, 2))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
function getErrorMessage(e: unknown): string {
  if (axios.isAxiosError(e) && e.response?.data) {
    if (typeof e.response.data === 'string') return e.response.data;
    return (e.response.data as { error?: string }).error ?? JSON.stringify(e.response.data);
  }
  return (e as Error).message;
}

type ResultSortKey =
  | 'ClassRank'
  | 'CarNumber'
  | 'TeamInstitution'
  | 'VehicleClass'
  | 'OnTrackLaps'
  | 'CorrectedTime'
  | 'AvgLapTime'
  | 'TotalPenaltyTime'
  | 'TotalPenaltyPoints'
  | 'FinalScore';

const RESULT_COLUMNS: {
  label: string;
  sortKey?: ResultSortKey;
  definitionKey?: string;
  align?: 'right';
  width?: number;
}[] = [
  { label: 'Details', width: 70, definitionKey: 'Expand' },
  { label: 'Rank', sortKey: 'ClassRank' },
  { label: 'Car', sortKey: 'CarNumber' },
  { label: 'Team / Institution', sortKey: 'TeamInstitution' },
  { label: 'Class', sortKey: 'VehicleClass' },
  { label: 'Laps', sortKey: 'OnTrackLaps', align: 'right' },
  { label: 'Corrected', sortKey: 'CorrectedTime', align: 'right' },
  { label: 'Avg Lap', sortKey: 'AvgLapTime', align: 'right' },
  { label: 'Penalty Time', sortKey: 'TotalPenaltyTime', align: 'right' },
  { label: 'Penalty Pts', sortKey: 'TotalPenaltyPoints', align: 'right' },
  { label: 'Final Score', sortKey: 'FinalScore', align: 'right' },
];

function resultSortValue(row: ScoringResultRow, key: ResultSortKey): unknown {
  switch (key) {
    case 'TeamInstitution':
      return `${row.TeamName ?? ''} ${row.Institution}`;
    case 'VehicleClass':
      return row.VehicleClass;
    case 'ClassRank':
    case 'CarNumber':
    case 'OnTrackLaps':
      return row[key];
    case 'CorrectedTime':
    case 'AvgLapTime':
    case 'TotalPenaltyTime':
    case 'TotalPenaltyPoints':
    case 'FinalScore':
      return num(row[key]);
    default:
      return null;
  }
}

const OrgScoring: React.FC = () => {
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<ScoringTab>('ALL');
  const [selectedLapCar, setSelectedLapCar] = useState<LiveBoardRow | null>(null);
  const [resultSort, setResultSort] = useState<SortState<ResultSortKey> | null>(null);
  const [expandedCar, setExpandedCar] = useState<number | null>(null);
  const [inputChangesSinceCalc, setInputChangesSinceCalc] = useState(0);
  const [wsLastCalcAt, setWsLastCalcAt] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<CalculateResponse | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  // ---- Queries -----------------------------------------------------------
  const resultsQuery = useQuery({
    queryKey: ['scoringResults'],
    queryFn: async () =>
      (await axios.get<ScoringResultRow[]>(`${API_BASE}/api/scoring/results`)).data,
  });
  const lapSummaryQuery = useQuery({
    queryKey: ['lapSummary'],
    queryFn: async () =>
      (await axios.get<LapSummaryRow[]>(`${API_BASE}/api/scoring/lapSummary`)).data,
  });
  const penaltySummaryQuery = useQuery({
    queryKey: ['penaltySummary'],
    queryFn: async () =>
      (await axios.get<PenaltySummaryRow[]>(`${API_BASE}/api/scoring/penaltySummary`)).data,
  });
  const allCarsQuery = useQuery({
    queryKey: ['scoringAllCarsBoard'],
    queryFn: async () =>
      (await axios.get<LiveBoardRow[]>(`${API_BASE}/api/scoring/allCarsBoard`)).data,
  });
  const lapsByCarQuery = useQuery({
    queryKey: ['scoringLapsByCar', selectedLapCar?.CarNumber],
    queryFn: async () =>
      (await axios.get<LapDetailRow[]>(
        `${API_BASE}/api/scoring/laps/${selectedLapCar?.CarNumber}`,
      )).data,
    enabled: selectedLapCar != null,
  });

  const results = resultsQuery.data ?? [];
  const allCars = useMemo(
    () => (allCarsQuery.data ?? []).slice().sort((a, b) => a.CarNumber - b.CarNumber),
    [allCarsQuery.data],
  );
  const lapByCar = useMemo(() => {
    const m = new Map<number, LapSummaryRow>();
    (lapSummaryQuery.data ?? []).forEach((r) => m.set(r.CarNumber, r));
    return m;
  }, [lapSummaryQuery.data]);
  const penaltyByCar = useMemo(() => {
    const m = new Map<number, PenaltySummaryRow>();
    (penaltySummaryQuery.data ?? []).forEach((r) => m.set(r.CarNumber, r));
    return m;
  }, [penaltySummaryQuery.data]);

  // ---- Last-calculated timestamp ----------------------------------------
  // Use the websocket-announced timestamp if we've seen one, otherwise fall
  // back to max(CalculatedAt) from the loaded rows. Keeps the header honest
  // across manual clicks, browser reloads, and background mutations.
  const lastCalcAt = useMemo(() => {
    if (wsLastCalcAt) return wsLastCalcAt;
    let latest: string | null = null;
    for (const r of results) {
      if (r.CalculatedAt && (!latest || r.CalculatedAt > latest)) {
        latest = r.CalculatedAt;
      }
    }
    return latest;
  }, [results, wsLastCalcAt]);

  // ---- Websocket: staleness + auto-refetch on calc ----------------------
  // 'scoring' carries `{ event: 'calculated', ... }` emitted by the
  // calculate route. 'pit' / 'penalty' / 'allCarsBoard' cover every
  // mutation that would alter a ScoringResults row. Any of those after
  // the last calc marks results as stale.
  const onWsMessage = useCallback(
    (msg: { channel: string; message: unknown }) => {
      if (msg.channel === 'scoring') {
        const body = msg.message as ScoringWsMessage;
        if (body.event === 'calculated') {
          setWsLastCalcAt(
            typeof body.at === 'string' ? body.at : new Date().toISOString(),
          );
          setInputChangesSinceCalc(0);
          queryClient.invalidateQueries({ queryKey: ['scoringResults'] });
          queryClient.invalidateQueries({ queryKey: ['lapSummary'] });
          queryClient.invalidateQueries({ queryKey: ['penaltySummary'] });
          queryClient.invalidateQueries({ queryKey: ['scoringLapsByCar'] });
        }
        return;
      }
      // Any input-side channel: bump the stale counter. We don't refetch
      // results here — stale scoring is the whole point of the banner.
      setInputChangesSinceCalc((n) => n + 1);
    },
    [queryClient],
  );
  useWebSocketHandler(['scoring', 'pit', 'penalty', 'allCarsBoard'], onWsMessage);

  // ---- Run scoring -------------------------------------------------------
  const runScoring = async () => {
    setRunning(true);
    setRunError(null);
    setRunResult(null);
    try {
      const { data } = await axios.post<CalculateResponse>(
        `${API_BASE}/api/scoring/calculate`,
      );
      setRunResult(data);
      // The 'scoring' WS message will invalidate the queries, but we
      // kick off an immediate refetch as well so the table refreshes
      // even if the websocket round-trip lags.
      queryClient.invalidateQueries({ queryKey: ['scoringResults'] });
      queryClient.invalidateQueries({ queryKey: ['lapSummary'] });
      queryClient.invalidateQueries({ queryKey: ['penaltySummary'] });
      queryClient.invalidateQueries({ queryKey: ['scoringLapsByCar'] });
      setWsLastCalcAt(new Date().toISOString());
      setInputChangesSinceCalc(0);
    } catch (e) {
      setRunError(getErrorMessage(e));
    } finally {
      setRunning(false);
    }
  };

  // ---- Filtering + rendering --------------------------------------------
  const visibleRows = useMemo(() => {
    if (tab === 'LAPS') return [];
    const filtered =
      tab === 'ALL' ? results : results.filter((r) => r.VehicleClass === tab);
    if (resultSort) return sortByState(filtered, resultSort, resultSortValue);
    // Sort by ClassRank (nulls last), then by FinalScore desc as a tiebreaker.
    return [...filtered].sort((a, b) => {
      const ar = a.ClassRank ?? Number.MAX_SAFE_INTEGER;
      const br = b.ClassRank ?? Number.MAX_SAFE_INTEGER;
      if (ar !== br) return ar - br;
      return (num(b.FinalScore) ?? 0) - (num(a.FinalScore) ?? 0);
    });
  }, [results, resultSort, tab]);

  const rowsByClass = useMemo(() => {
    if (tab !== 'ALL' || resultSort) return null;
    const groups = new Map<string, ScoringResultRow[]>();
    for (const r of visibleRows) {
      const key = r.VehicleClass ?? 'Unclassified';
      const arr = groups.get(key) ?? [];
      arr.push(r);
      groups.set(key, arr);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [resultSort, visibleRows, tab]);

  const loading =
    resultsQuery.isLoading || lapSummaryQuery.isLoading || penaltySummaryQuery.isLoading;

  // ---- Export menu -------------------------------------------------------
  const openExport = (e: React.MouseEvent<HTMLElement>) => setExportAnchor(e.currentTarget);
  const closeExport = () => setExportAnchor(null);
  const download = (path: string) => {
    window.open(`${API_BASE}${path}`, '_blank', 'noopener');
    closeExport();
  };

  // ---- Render -----------------------------------------------------------
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Paper
        sx={{
          m: 1,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Header: class tabs */}
        <Tabs
          value={tab}
          onChange={(_e, v: ScoringTab) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="ALL" label={`All (${results.length})`} />
          <Tab
            value="H"
            label={`Class H (${results.filter((r) => r.VehicleClass === 'H').length})`}
          />
          <Tab
            value="E"
            label={`Class E (${results.filter((r) => r.VehicleClass === 'E').length})`}
          />
          <Tab value="LAPS" label="Laps Per Car" />
        </Tabs>

        {/* Header: last-calc + actions */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          sx={{ p: { xs: 1, sm: 2 } }}
        >
          <Typography variant="body2" color="text.secondary">
            <DefinedLabel label="Last calculated" />:{' '}
            <Box component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {lastCalcAt ? ConvertTime(lastCalcAt) : 'Never'}
            </Box>
          </Typography>
          {inputChangesSinceCalc > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`Stale — ${inputChangesSinceCalc} change${inputChangesSinceCalc === 1 ? '' : 's'} since last calc`}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={runScoring}
            disabled={running}
            sx={{ minWidth: 160 }}
          >
            {running ? 'Running…' : 'Run Scoring'}
          </Button>
          <Button
            variant="outlined"
            onClick={openExport}
            startIcon={<DownloadIcon />}
            disabled={results.length === 0}
          >
            Export
          </Button>
          <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={closeExport}>
            <MenuItem onClick={() => download('/api/scoring/export/carSummary.csv')}>
              Car Summary (CSV)
            </MenuItem>
            <MenuItem onClick={() => download('/api/scoring/export/laps.csv')}>
              Raw Laps (CSV)
            </MenuItem>
            <MenuItem onClick={() => download('/api/scoring/export/penaltySummary.csv')}>
              Penalty Summary (CSV)
            </MenuItem>
          </Menu>
        </Stack>

        {(runError || runResult) && (
          <Box sx={{ px: { xs: 1, sm: 2 }, pb: 1 }}>
            {runError && (
              <Alert severity="error" onClose={() => setRunError(null)}>
                {runError}
              </Alert>
            )}
            {runResult && (
              <Alert
                severity={runResult.skipped.length > 0 ? 'warning' : 'success'}
                onClose={() => setRunResult(null)}
              >
                Scored {runResult.calculated} car{runResult.calculated === 1 ? '' : 's'}
                {runResult.skipped.length > 0 && (
                  <>
                    {' '}· Skipped {runResult.skipped.length}:{' '}
                    {runResult.skipped
                      .map((s) => `#${s.CarNumber} (${s.reason})`)
                      .join(', ')}
                  </>
                )}
              </Alert>
            )}
          </Box>
        )}

        {/* Body: leaderboard table / lap detail table */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {tab === 'LAPS' ? (
            <LapsPerCarPanel
              cars={allCars}
              selectedCar={selectedLapCar}
              onCarChange={setSelectedLapCar}
              rows={lapsByCarQuery.data ?? []}
              carsLoading={allCarsQuery.isLoading}
              lapsLoading={lapsByCarQuery.isLoading}
            />
          ) : (
            <TableContainer sx={{ height: '100%', minHeight: 0 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {RESULT_COLUMNS.map((h, i) => (
                    <TableCell
                      key={i}
                      align={h.align}
                      sx={{
                        fontWeight: 600,
                        bgcolor: 'dimgrey',
                        color: 'white',
                        width: h.width,
                      }}
                      sortDirection={
                        h.sortKey && resultSort?.key === h.sortKey
                          ? resultSort.direction
                          : false
                      }
                    >
                      {h.sortKey ? (
                        <TableSortLabel
                          active={resultSort?.key === h.sortKey}
                          direction={resultSort?.key === h.sortKey ? resultSort.direction : 'asc'}
                          onClick={() =>
                            setResultSort((current) => nextSortState(current, h.sortKey!))
                          }
                          sx={{
                            color: 'inherit',
                            '&:hover': { color: 'inherit' },
                            '&.Mui-active': { color: 'inherit' },
                            '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                          }}
                        >
                          <DefinedLabel label={h.label} definitionKey={h.definitionKey ?? h.label} />
                        </TableSortLabel>
                      ) : (
                        <DefinedLabel label={h.label} definitionKey={h.definitionKey ?? h.label} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && results.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ color: 'text.secondary' }}>
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ color: 'text.secondary' }}>
                      No results yet. Click <strong>Run Scoring</strong> to calculate.
                    </TableCell>
                  </TableRow>
                ) : tab === 'ALL' && rowsByClass ? (
                  rowsByClass.flatMap(([className, rows]) => [
                    <TableRow key={`hdr-${className}`}>
                      <TableCell
                        colSpan={11}
                        sx={{
                          bgcolor: 'action.selected',
                          fontWeight: 600,
                          py: 0.75,
                        }}
                      >
                        Class {className} · {rows.length} car{rows.length === 1 ? '' : 's'}
                      </TableCell>
                    </TableRow>,
                    ...rows.map((row) => (
                      <ResultRows
                        key={row.CarNumber}
                        row={row}
                        lap={lapByCar.get(row.CarNumber)}
                        penalty={penaltyByCar.get(row.CarNumber)}
                        expanded={expandedCar === row.CarNumber}
                        onToggle={() =>
                          setExpandedCar((prev) =>
                            prev === row.CarNumber ? null : row.CarNumber,
                          )
                        }
                      />
                    )),
                  ])
                ) : (
                  visibleRows.map((row) => (
                    <ResultRows
                      key={row.CarNumber}
                      row={row}
                      lap={lapByCar.get(row.CarNumber)}
                      penalty={penaltyByCar.get(row.CarNumber)}
                      expanded={expandedCar === row.CarNumber}
                      onToggle={() =>
                        setExpandedCar((prev) =>
                          prev === row.CarNumber ? null : row.CarNumber,
                        )
                      }
                    />
                  ))
                )}
              </TableBody>
            </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Per-car lap detail renderer. Mirrors the Laps table shape so organizers can
// inspect the raw lap records while keeping times in display-friendly formats.
// ---------------------------------------------------------------------------
interface LapsPerCarPanelProps {
  cars: LiveBoardRow[];
  selectedCar: LiveBoardRow | null;
  onCarChange: (car: LiveBoardRow | null) => void;
  rows: LapDetailRow[];
  carsLoading: boolean;
  lapsLoading: boolean;
}

function formatBooleanCell(v: number | boolean | null | undefined): string {
  if (v === true || v === 1) return 'yes';
  if (v === false || v === 0) return 'no';
  return '—';
}

function formatFlags(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

type LapSortKey =
  | 'Recnum'
  | 'CarNumber'
  | 'Transponder'
  | 'LapNumber'
  | 'LapTime'
  | 'StartTime'
  | 'EndTime'
  | 'LapType'
  | 'IsFree'
  | 'InTimeWindow'
  | 'ElapsedFromStart'
  | 'Flags';

const LAP_COLUMNS: { label: string; sortKey: LapSortKey }[] = [
  { label: 'Recnum', sortKey: 'Recnum' },
  { label: 'Car', sortKey: 'CarNumber' },
  { label: 'Transponder', sortKey: 'Transponder' },
  { label: 'Lap #', sortKey: 'LapNumber' },
  { label: 'LapTime', sortKey: 'LapTime' },
  { label: 'StartTime', sortKey: 'StartTime' },
  { label: 'EndTime', sortKey: 'EndTime' },
  { label: 'LapType', sortKey: 'LapType' },
  { label: 'IsFree', sortKey: 'IsFree' },
  { label: 'InTimeWindow', sortKey: 'InTimeWindow' },
  { label: 'ElapsedFromStart', sortKey: 'ElapsedFromStart' },
  { label: 'Flags', sortKey: 'Flags' },
];

function lapSortValue(row: LapDetailRow, key: LapSortKey): unknown {
  if (key === 'Flags') return formatFlags(row.Flags);
  return row[key];
}

function toClientCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function lapCsvValue(row: LapDetailRow, key: LapSortKey): string | number {
  switch (key) {
    case 'LapTime':
      return formatDurationTime(row.LapTime);
    case 'StartTime':
      return ConvertTime(row.StartTime);
    case 'EndTime':
      return ConvertTime(row.EndTime);
    case 'IsFree':
      return formatBooleanCell(row.IsFree);
    case 'InTimeWindow':
      return formatBooleanCell(row.InTimeWindow);
    case 'ElapsedFromStart':
      return formatDurationTime(row.ElapsedFromStart);
    case 'Flags':
      return formatFlags(row.Flags);
    default:
      return row[key] as string | number;
  }
}

interface ExtraLapCsvColumn {
  label: string;
  value: (row: LapDetailRow) => string | number;
}

function downloadLapCsv(
  car: LiveBoardRow,
  rows: LapDetailRow[],
  suffix = 'laps',
  extraColumns: ExtraLapCsvColumn[] = [],
) {
  const header = [...LAP_COLUMNS.map((c) => c.label), ...extraColumns.map((c) => c.label)];
  const lines = rows.map((row) =>
    [
      ...LAP_COLUMNS.map((column) => lapCsvValue(row, column.sortKey)),
      ...extraColumns.map((column) => column.value(row)),
    ].map(toClientCsvCell).join(','),
  );
  const definitions = [
    '',
    'Definitions',
    'Column,Definition',
    ...LAP_COLUMNS.map((column) =>
      `${toClientCsvCell(column.label)},${toClientCsvCell(FIELD_DEFINITIONS[column.label] ?? 'No definition available.')}`,
    ),
    ...extraColumns.map((column) =>
      `${toClientCsvCell(column.label)},${toClientCsvCell(FIELD_DEFINITIONS[column.label] ?? 'No definition available.')}`,
    ),
  ];
  const csv = `${header.join(',')}\r\n${lines.join('\r\n')}\r\n${definitions.join('\r\n')}\r\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `car_${car.CarNumber}_${suffix}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function buildRenumberedLapMap(rows: LapDetailRow[]): Map<number, number> {
  const numbers = new Map<number, number>();
  let nextLapNumber = 1;
  for (const row of rows) {
    if (row.LapType === 'PIT_DC') continue;
    numbers.set(row.Recnum, nextLapNumber);
    nextLapNumber++;
  }
  return numbers;
}

function LapsPerCarPanel({
  cars,
  selectedCar,
  onCarChange,
  rows,
  carsLoading,
  lapsLoading,
}: LapsPerCarPanelProps) {
  const [checkedRows, setCheckedRows] = useState<Set<number>>(() => new Set());
  const [lapExportAnchor, setLapExportAnchor] = useState<HTMLElement | null>(null);
  const [showRenumberedLaps, setShowRenumberedLaps] = useState(false);
  const [lapSort, setLapSort] = useState<SortState<LapSortKey> | null>({
    key: 'LapNumber',
    direction: 'asc',
  });
  const sortedRows = useMemo(
    () => sortByState(rows, lapSort, lapSortValue),
    [lapSort, rows],
  );
  const checkedSortedRows = useMemo(
    () => sortedRows.filter((row) => checkedRows.has(row.Recnum)),
    [checkedRows, sortedRows],
  );
  const renumberedLapNumbers = useMemo(
    () => buildRenumberedLapMap(checkedSortedRows),
    [checkedSortedRows],
  );
  const tableColumnCount = LAP_COLUMNS.length + 1 + (showRenumberedLaps ? 1 : 0);
  const closeLapExport = () => setLapExportAnchor(null);
  const toggleChecked = (recnum: number) => {
    setCheckedRows((current) => {
      const next = new Set(current);
      if (next.has(recnum)) {
        next.delete(recnum);
      } else {
        next.add(recnum);
      }
      return next;
    });
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <Autocomplete
          sx={{ maxWidth: 420, minWidth: 260, flex: 1 }}
          options={cars}
          getOptionLabel={(c) => `${c.CarNumber} — ${c.Institution}`}
          filterOptions={carFilter}
          isOptionEqualToValue={(a, b) => a.CarNumber === b.CarNumber}
          value={selectedCar}
          onChange={(_e, v) => onCarChange(v)}
          autoHighlight
          openOnFocus
          selectOnFocus
          loading={carsLoading}
          renderInput={(params) => (
            <TextField {...params} label="Car" placeholder="Search car number or institution" />
          )}
        />
        <Button
          variant={showRenumberedLaps ? 'contained' : 'outlined'}
          disabled={!selectedCar || lapsLoading || checkedSortedRows.length === 0}
          onClick={() => setShowRenumberedLaps((current) => !current)}
        >
          {showRenumberedLaps ? 'Hide Re-numbering' : 'Re-number Laps'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          disabled={!selectedCar || lapsLoading || sortedRows.length === 0}
          onClick={(event) => setLapExportAnchor(event.currentTarget)}
        >
          Export CSV
        </Button>
        <Menu anchorEl={lapExportAnchor} open={!!lapExportAnchor} onClose={closeLapExport}>
          <MenuItem
            onClick={() => {
              if (selectedCar) downloadLapCsv(selectedCar, sortedRows);
              closeLapExport();
            }}
          >
            All rows
          </MenuItem>
          <MenuItem
            disabled={checkedSortedRows.length === 0}
            onClick={() => {
              if (selectedCar) {
                downloadLapCsv(
                  selectedCar,
                  checkedSortedRows,
                  'checked_laps',
                  showRenumberedLaps
                    ? [{
                        label: RENUMBERED_LAP_COLUMN_LABEL,
                        value: (row) => renumberedLapNumbers.get(row.Recnum) ?? '',
                      }]
                    : [],
                );
              }
              closeLapExport();
            }}
          >
            Checked rows ({checkedSortedRows.length})
          </MenuItem>
        </Menu>
      </Stack>

      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {LAP_COLUMNS.map((h) => (
                <TableCell
                  key={h.sortKey}
                  sx={{ fontWeight: 600, bgcolor: 'dimgrey', color: 'white' }}
                  sortDirection={lapSort?.key === h.sortKey ? lapSort.direction : false}
                >
                  <TableSortLabel
                    active={lapSort?.key === h.sortKey}
                    direction={lapSort?.key === h.sortKey ? lapSort.direction : 'asc'}
                    onClick={() => setLapSort((current) => nextSortState(current, h.sortKey))}
                    sx={{
                      color: 'inherit',
                      '&:hover': { color: 'inherit' },
                      '&.Mui-active': { color: 'inherit' },
                      '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                    }}
                  >
                    <DefinedLabel label={h.label} />
                  </TableSortLabel>
                </TableCell>
              ))}
              {showRenumberedLaps && (
                <TableCell sx={{ fontWeight: 600, bgcolor: 'dimgrey', color: 'white' }}>
                  <DefinedLabel label={RENUMBERED_LAP_COLUMN_LABEL} />
                </TableCell>
              )}
              <TableCell
                sx={{
                  fontWeight: 600,
                  bgcolor: 'dimgrey',
                  color: 'white',
                  position: 'sticky',
                  right: 0,
                  zIndex: 3,
                }}
              >
                Check
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!selectedCar ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount} align="center" sx={{ color: 'text.secondary' }}>
                  Select a car to view its completed laps.
                </TableCell>
              </TableRow>
            ) : lapsLoading ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount} align="center" sx={{ color: 'text.secondary' }}>
                  Loading laps…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColumnCount} align="center" sx={{ color: 'text.secondary' }}>
                  No completed laps for car {selectedCar.CarNumber}.
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row) => {
                const isChecked = checkedRows.has(row.Recnum);
                return (
                  <TableRow
                    key={row.Recnum}
                    hover
                    sx={
                      isChecked
                        ? {
                            bgcolor: CHECKED_ROW_BG,
                            '&:hover': { bgcolor: CHECKED_ROW_HOVER_BG },
                          }
                        : undefined
                    }
                  >
                    <TableCell>{row.Recnum}</TableCell>
                    <TableCell>{row.CarNumber}</TableCell>
                    <TableCell>{row.Transponder}</TableCell>
                    <TableCell>{row.LapNumber}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {formatDurationTime(row.LapTime)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {ConvertTime(row.StartTime)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {ConvertTime(row.EndTime)}
                    </TableCell>
                    <TableCell>{row.LapType}</TableCell>
                    <TableCell>{formatBooleanCell(row.IsFree)}</TableCell>
                    <TableCell>{formatBooleanCell(row.InTimeWindow)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {formatDurationTime(row.ElapsedFromStart)}
                    </TableCell>
                    <TableCell>{formatFlags(row.Flags)}</TableCell>
                    {showRenumberedLaps && (
                      <TableCell align="right">
                        {renumberedLapNumbers.get(row.Recnum) ?? ''}
                      </TableCell>
                    )}
                    <TableCell
                      align="center"
                      padding="checkbox"
                      sx={{
                        position: 'sticky',
                        right: 0,
                        zIndex: 1,
                        bgcolor: isChecked ? CHECKED_ROW_BG : 'background.paper',
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        onChange={() => toggleChecked(row.Recnum)}
                        sx={{
                          '&.Mui-checked': { color: CHECKBOX_YELLOW },
                        }}
                        inputProps={{ 'aria-label': `Highlight lap row ${row.Recnum}` }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Single-result renderer (summary row + collapsible audit row).
// Extracted so the two tab layouts (grouped / flat) can reuse it without
// duplicating state wiring.
// ---------------------------------------------------------------------------
interface ResultRowsProps {
  row: ScoringResultRow;
  lap: LapSummaryRow | undefined;
  penalty: PenaltySummaryRow | undefined;
  expanded: boolean;
  onToggle: () => void;
}

function ResultRows({ row, lap, penalty, expanded, onToggle }: ResultRowsProps) {
  return (
    <React.Fragment>
      <TableRow hover onClick={onToggle} sx={{ cursor: 'pointer' }}>
        <TableCell sx={{ width: 40, py: 0 }}>
          <IconButton size="small" aria-label={expanded ? 'Collapse' : 'Expand'}>
            {expanded ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>{row.ClassRank ?? '—'}</TableCell>
        <TableCell sx={{ fontWeight: 500 }}>{row.CarNumber}</TableCell>
        <TableCell>
          <Stack spacing={0} sx={{ lineHeight: 1.2 }}>
            {row.TeamName && (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {row.TeamName}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {row.Institution}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>{row.VehicleClass ?? '—'}</TableCell>
        <TableCell align="right">{row.OnTrackLaps ?? '—'}</TableCell>
        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
          {formatLongTime(row.CorrectedTime)}
        </TableCell>
        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
          {formatLapTime(row.AvgLapTime)}
        </TableCell>
        <TableCell align="right">{formatNumber(row.TotalPenaltyTime, 1)}</TableCell>
        <TableCell align="right">{formatNumber(row.TotalPenaltyPoints, 1)}</TableCell>
        <TableCell
          align="right"
          sx={{ fontWeight: 600, fontFamily: 'monospace' }}
        >
          {formatNumber(row.FinalScore, 2)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <AuditRow row={row} lap={lap} penalty={penalty} />
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default OrgScoring;
